import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { fetchAccounts, selectAccounts } from 'features/snowflake-accounts/reducers/snowflakeAccountsSlice';
import { resetOnboarding } from 'features/onboarding/reducers/onboardingSlice';
import { signupApi, type SignupStatusResponse } from 'features/frictionless-signup/services/signupApi';
import { CpuChipIcon, PlusIcon } from '@heroicons/react/24/outline';
import SnowflakeIcon from 'components/SnowflakeIcon';
import RunnerStatusDisplay from 'components/RunnerStatusDisplay';
import NextActionBox from 'components/NextActionBox';

/**
 * DashboardView Component
 *
 * Main dashboard landing page that displays:
 * - Overview of Snowflake accounts
 * - Overview of DataOps runners
 * - Quick access to key features
 *
 * @component
 */
const DashboardView: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const accounts = useAppSelector(selectAccounts);
  const [signupStatus, setSignupStatus] = useState<SignupStatusResponse | null>(null);
  const [isLoadingSignupStatus, setIsLoadingSignupStatus] = useState(true);

  useEffect(() => {
    // Fetch Snowflake accounts
    dispatch(fetchAccounts()).catch((err) => {
      console.error('Failed to fetch accounts:', err);
    });

    // Fetch signup status
    const fetchSignupStatus = async (): Promise<void> => {
      try {
        setIsLoadingSignupStatus(true);
        const status = await signupApi.getCurrentUserSignupStatus();
        setSignupStatus(status);
      } catch (err) {
        console.error('Failed to fetch signup status:', err);
        setSignupStatus(null);
      } finally {
        setIsLoadingSignupStatus(false);
      }
    };

    void fetchSignupStatus();
  }, [dispatch]);

  const handleConnectAccountClick = (): void => {
    dispatch(resetOnboarding());
    navigate('/onboarding', { state: { step: 'snowflake_setup' } });
  };

  return (
    <div className="h-full min-w-0 flex-1 bg-dashboard-background">
      <div className="w-full min-w-0  lg:max-w-7xl sm:p-6 lg:p-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-2xl text-dataops-dark">Dashboard</h1>
          <p className="mt-2 text-sm text-secondary-text">Overview of your Snowflake accounts and DataOps runners</p>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Snowflake Accounts Section */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-dataops-primary-blue/10">
                  <SnowflakeIcon size="h-6" className="text-dataops-primary-blue" />
                </div>
                <h2 className="text-lg text-dataops-dark">Snowflake Accounts</h2>
              </div>
              <Link
                to="/snowflake-accounts"
                className="text-sm text-dataops-primary-blue hover:text-hover-primary-blue"
              >
                View all →
              </Link>
            </div>

            {accounts.length === 0 ? (
              <div className="text-center py-8">
                <SnowflakeIcon size="h-12" className="mx-auto text-gray-300 mb-3" />
                <p className="text-sm text-secondary-text mb-4">No Snowflake accounts connected yet</p>
                <button
                  type="button"
                  onClick={handleConnectAccountClick}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-dataops-primary-blue hover:bg-hover-primary-blue text-white text-sm rounded-md transition-colors"
                >
                  <PlusIcon className="h-4 w-4" />
                  Connect Account
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-md">
                  <span className="text-sm text-secondary-text">Total Accounts</span>
                  <span className="text-lg text-dataops-dark">{accounts.length}</span>
                </div>

                <div className="border-t border-gray-200 pt-3">
                  <h3 className="text-sm text-secondary-text mb-2">Recent Accounts</h3>
                  <div className="space-y-2">
                    {accounts.slice(0, 3).map((account) => (
                      <div
                        key={account.id}
                        className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-md hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <SnowflakeIcon size="h-4" className="text-dataops-primary-blue flex-shrink-0" />
                          <div className="min-w-0">
                            <p className="text-sm text-primary-text truncate">{account.account}</p>
                            <p className="text-xs text-secondary-text truncate">{account.username}</p>
                          </div>
                        </div>
                        <span className="text-xs text-secondary-text bg-white px-2 py-1 rounded flex-shrink-0">
                          {account.type}
                        </span>
                      </div>
                    ))}
                  </div>
                  {accounts.length > 3 && (
                    <Link
                      to="/snowflake-accounts"
                      className="block mt-2 text-center text-sm text-dataops-primary-blue hover:text-hover-primary-blue"
                    >
                      View {accounts.length - 3} more
                    </Link>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Runners Section */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-green-highlight/10">
                  <CpuChipIcon className="h-6 w-6 text-green-highlight" />
                </div>
                <h2 className="text-lg text-dataops-dark">DataOps Runners</h2>
              </div>
              <Link to="/runners" className="text-sm text-dataops-primary-blue hover:text-hover-primary-blue">
                View all →
              </Link>
            </div>

            {isLoadingSignupStatus ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-dataops-primary-blue mx-auto"></div>
                <p className="text-sm text-secondary-text mt-3">Loading runner status...</p>
              </div>
            ) : signupStatus === null ? (
              <div className="text-center py-8">
                <CpuChipIcon className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                <p className="text-sm text-dataops-dark mb-1">Runners managed by your organization</p>
                <p className="text-xs text-secondary-text">
                  Your DataOps runners are managed by your organization. Contact your administrator for more
                  information.
                </p>
              </div>
            ) : (
              <RunnerStatusDisplay status={signupStatus?.current_step ?? null} />
            )}
          </div>
        </div>

        {/* Next Action Box - only show if user has a Snowflake account */}
        {signupStatus?.has_snowflake_account === true && (
          <div className="mt-6">
            <NextActionBox userJourneyVariant={signupStatus?.app_variant} />
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardView;
